import React, { useState } from "react";
import { LoadingState } from "../utils/enum";
import { LoadsContextModel } from "../models/Load";
import { Driver, Equipment, LoadData } from "../models";
import {
  breakSealAPI,
  checkInDropOff,
  fetchLoads,
  markHeadingPickup,
} from "../services/loadAPI";
import { equipmentStatus } from "../utils/helper";

type LoadsProviderProps = {
  children?: React.ReactNode;
};

export const LoadsContext = React.createContext<LoadsContextModel>(
  {} as LoadsContextModel
);

export const LoadsProvider = ({ children }: LoadsProviderProps) => {
  const [tabView, setTabView] = useState(0);
  const [loads, setLoads] = useState<LoadData[]>([]);
  const [searchedLoads, setSearchedLoads] = useState<LoadData[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [detailDrawer, setDetailDrawer] = useState(false);
  const [selectedLoad, setSelectedLoad] = useState<LoadData>();
  const [loadingState, setLoadingState] = useState({
    0: LoadingState.None,
    1: LoadingState.None,
    2: LoadingState.None,
    3: LoadingState.None,
    4: LoadingState.None,
  } as Record<number, LoadingState>);
  const [loadingDetailsState, setLoadingDetailsState] = useState(
    LoadingState.Pending
  );

  const listLoads = async () => {
    if (
      [LoadingState.Pending, LoadingState.Success].includes(
        loadingState[tabView]
      )
    ) {
      return;
    }

    setLoadingState({ ...loadingState, [tabView]: LoadingState.Pending });
    try {
      const data = await fetchLoads({
        status: equipmentStatus[tabView].join(","),
      });
      console.log("loads data", data);
      setLoads([...data, ...loads]);
      setLoadingState({ ...loadingState, [tabView]: LoadingState.Success });
    } catch (err) {
      console.error("Failed to list loads", err);
      setLoadingState({ ...loadingState, [tabView]: LoadingState.Error });
    }
  };

  const updateLoadToHeadingPickup = async (loadID: string) => {
    await markHeadingPickup(loadID);
    updateLoadStatus(loadID, "awaiting_pickup");
  };

  const updateLoadTocheckInDropOff = async (loadID: string) => {
    await checkInDropOff(loadID);
    updateLoadStatus(loadID, "checked_in_dropoff", "at_dropoff");
  };

  const breakSeal = async (loadID: string, sealNum: string) => {
    await breakSealAPI(loadID, sealNum);
    updateLoadStatus(loadID, "sealed_and_ready");
  };

  const updateLoadStatus = (
    id: string,
    status: LoadData["status"],
    equipmentStatus?: string
  ) => {
    const updatedLoads = [...loads];
    const loadIndex = updatedLoads.findIndex((item) => item.loadID === id);
    if (loadIndex > -1) {
      updatedLoads[loadIndex].status = status;
      if (equipmentStatus) {
        updatedLoads[loadIndex].equipmentStatus = equipmentStatus;
        if (equipmentStatus === "at_dropoff") {
          setTabView(3);
        }
      }
      setLoads(updatedLoads);
    }
  };

  const updateLoadEquipment = (loadID: string, equipment: Equipment) => {
    const updatedLoads = [...loads];
    const index = updatedLoads.findIndex((item) => item.loadID === loadID);
    if (index > -1) {
      updatedLoads[index].equipmentId = equipment.equipmentID;
      updatedLoads[index].equipmentNum = equipment.num;
      updatedLoads[index].equipmentType = equipment.type;
      updatedLoads[index].lp = equipment.lp;
      updatedLoads[index].carrierEquipmentID = equipment.equipmentID;
      updatedLoads[index].carrierEquipmentLp = equipment.lp;
      updatedLoads[index].carrierEquipmentNum = equipment.num;
      updatedLoads[index].carrierEquipmentType = equipment.type;
      setLoads(updatedLoads);
    }
  };

  const appendDriverToLoad = (driver: Driver) => {
    const updatedLoads = [...loads];
    const loadIndex = updatedLoads.findIndex(
      (item) => item.loadID === selectedLoad?.loadID
    );
    if (loadIndex > -1) {
      updatedLoads[loadIndex].driver = driver;
      if (updatedLoads[loadIndex].status === "unassigned") {
        updatedLoads[loadIndex].status = "assigned";
      }
      setLoads(updatedLoads);
    }
  };

  const searchLoads = (term: string) => {
    setSearchTerm(term);
    const lower = term.toLowerCase();
    const filtered = loads.filter(
      (l) =>
        l.pickupLocation.name.toLowerCase().includes(lower) ||
        l.dropOffLocation.name.toLowerCase().includes(lower) ||
        l.loadNumID.toLowerCase().includes(lower) ||
        l.equipmentNum?.toLowerCase().includes(lower) ||
        l.carrierEquipmentLp?.toLowerCase().includes(lower) ||
        l.carrierEquipmentNum?.toLowerCase().includes(lower) ||
        l.carrierEquipmentType?.toLowerCase().includes(lower) ||
        l.equipmentType?.toLowerCase().includes(lower) ||
        l.lp?.toLowerCase().includes(lower) ||
        l.driver?.name.toLowerCase().includes(lower) ||
        l.driver?.email.toLowerCase().includes(lower)
    );
    setSearchedLoads(filtered);
  };

  const searchDrivers = () => {}; // stub

  const listDrivers = async () => {}; // stub

  const clearState = () => {
    setSearchTerm("");
    setSearchedLoads([]);
    setLoads([]);
    setTabView(0);
    setLoadingState({
      0: LoadingState.None,
      1: LoadingState.None,
      2: LoadingState.None,
      3: LoadingState.None,
      4: LoadingState.None,
    });
  };

  // const searchBids = async (term: string) => {
  //   try {
  //     term = term.trim().toLowerCase();
  //     const bids = [
  //       ...(bidsTabViewIndex === 0
  //         ? pendingBids
  //         : bidsTabViewIndex === 1
  //         ? acceptedBids
  //         : rejectedBids),
  //     ];
  //     // Implement search locally
  //     const searchedItems =
  //       term.length === 0
  //         ? []
  //         : bids.filter((bid) => {
  //             const isValid =
  //               bid.shipperName?.toLowerCase().includes(term) ||
  //               bid.distance.toString().toLowerCase().includes(term) ||
  //               bid.loadData.pickupLocation.name.toLowerCase().includes(term) ||
  //               bid.loadData.dropOffLocation.name
  //                 .toLowerCase()
  //                 .includes(term) ||
  //               bid.totalRevenue.toString().toLowerCase().includes(term) ||
  //               bid.askPrice.toString().toLowerCase().includes(term) ||
  //               bid.matchPrice.toString().toLowerCase().includes(term);
  //             return isValid;
  //           });
  //     setSearchTerm(term);
  //     setSearchedBids(searchedItems);
  //   } catch (error) {
  //     console.error("error searhing", error);
  //   }
  // };

  // const listPendingBids = async () => {
  //   try {
  //     setLoadingState(LoadingState.Pending);
  //     await readBidNotifications();
  //     const data = await fetchPendingBids();
  //     setPendingBids(data);
  //     setLoadingState(LoadingState.Success);
  //   } catch (e) {
  //     setLoadingState(LoadingState.Error);
  //   }
  // };

  const values: LoadsContextModel = {
    searchDrivers,
    appendDriverToLoad,
    searchLoads,
    listDrivers,
    listLoads,
    clearState,
    updateLoadTocheckInDropOff,
    loadingDetailsState,
    loads,
    searchTerm,
    searchedLoads,
    loadingState,
    updateLoadEquipment,
    setDetailDrawer,
    setLoadingDetailsState,
    setLoadingState,
    setSelectedLoad,
    setTabView,
    updateLoadStatus,
    updateLoadToHeadingPickup,
    breakSeal,
    selectedLoad,
    tabView,
    detailDrawer,
  };

  return (
    <LoadsContext.Provider value={values}>{children}</LoadsContext.Provider>
  );
};
